<script setup lang="ts">
import { computed } from 'vue';
import FireNumberTooltip from './FireNumberTooltip.vue';

interface Props {
  tone: 'traditional' | 'dwz';
  title: string;
  subheader: string;
  heroLabel: string;
  heroValue: string;
  heroSuffix?: string;
  heroUnit?: string;
  bridgeText: string;
  fireNumber: string;
  fireNumberTooltip?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  heroUnit: '',
  fireNumberTooltip: '',
});

const resolvedFireNumberTooltip = computed(() => props.fireNumberTooltip ?? '');

function textTone(tone: 'traditional' | 'dwz'): string {
  return tone === 'traditional' ? 'text-trad-500' : 'text-dwz-500';
}

function containerTone(tone: 'traditional' | 'dwz'): string {
  return tone === 'traditional' ? 'bg-trad-50' : 'bg-dwz-50';
}

function borderTone(tone: 'traditional' | 'dwz'): string {
  return tone === 'traditional' ? 'border-trad-300' : 'border-dwz-300';
}

function accentTone(tone: 'traditional' | 'dwz'): string {
  return tone === 'traditional' ? 'bg-trad-300' : 'bg-dwz-300';
}
</script>

<template>
  <article
    class="bg-bg-card rounded-extra-large border border-border-light overflow-hidden"
  >
    <div class="h-0.5" :class="accentTone(tone)" />
    <div class="p-lg">
      <header class="flex items-start gap-md">
        <div
          class="w-10 h-10 rounded-medium flex items-center justify-center"
          :class="containerTone(tone)"
        >
          <svg
            v-if="tone === 'traditional'"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            :class="textTone(tone)"
          >
            <path d="M3 12a9 9 0 0 1 18 0 9 9 0 0 1-18 0" />
            <path d="M3 12c4 0 6-4 6-9 0 5 2 9 6 9 4 0 6-4 6-9" />
          </svg>
          <svg
            v-else
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            :class="textTone(tone)"
          >
            <path d="M5 22h14" />
            <path d="M5 2h14" />
            <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
            <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <h3 class="text-title-large font-semibold" :class="textTone(tone)">{{ title }}</h3>
          <p class="text-body-medium text-grey-700">{{ subheader }}</p>
        </div>
      </header>

      <p
        class="mt-lg text-label-medium font-semibold uppercase text-text-secondary tracking-wide"
      >
        {{ heroLabel }}
      </p>

      <div
        class="mt-md rounded-medium border-b-2 px-lg py-sm flex items-baseline justify-center gap-1.5"
        :class="[containerTone(tone), borderTone(tone)]"
      >
        <span class="text-headline-large font-bold leading-tight" :class="textTone(tone)">{{ heroValue }}</span>
        <span v-if="heroSuffix" class="text-headline-small font-medium leading-tight" :class="textTone(tone)">{{ heroSuffix }}</span>
        <span v-if="heroUnit" class="text-label-small font-medium" :class="textTone(tone)">{{ heroUnit }}</span>
      </div>

      <div class="mt-xl flex items-baseline justify-between gap-1.5">
        <span class="text-body-small text-text-secondary">{{ bridgeText }}</span>
        <FireNumberTooltip
          v-if="resolvedFireNumberTooltip"
          :text="resolvedFireNumberTooltip"
        >
          <span
            class="text-headline-small font-medium text-text-primary underline decoration-dotted underline-offset-4"
          >{{ fireNumber }}</span>
        </FireNumberTooltip>
        <span
          v-else
          class="text-headline-small font-medium text-text-primary"
        >{{ fireNumber }}</span>
      </div>
    </div>
  </article>
</template>
